import React, { useEffect, useState } from 'react';
import { openWebPolicy } from '../../utils/webAppBridge';
import { changePasswordByCccd } from '../../services/authApi';

type Props = {
  pushOn: boolean;
  pushBusy: boolean;
  onTogglePush: () => void;
  onLogout: () => void;
};

const MobileSettingsControl: React.FC<Props> = ({
  pushOn,
  pushBusy,
  onTogglePush,
  onLogout,
}) => {
  const [open, setOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [cccd, setCccd] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open && !passwordOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setPasswordOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, passwordOpen]);

  const resetPasswordForm = () => {
    setCccd('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSubmitting(false);
    setSuccess(false);
  };

  const closePasswordForm = () => {
    setPasswordOpen(false);
    resetPasswordForm();
  };

  const openPasswordForm = () => {
    setOpen(false);
    resetPasswordForm();
    setPasswordOpen(true);
  };

  const handleChangePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    const digits = cccd.replace(/\D/g, '');
    if (digits.length !== 9 && digits.length !== 12) {
      setError('CCCD phải gồm 9 hoặc 12 chữ số');
      return;
    }
    if (newPassword.length < 8) {
      setError('Mật khẩu mới phải có ít nhất 8 ký tự');
      return;
    }
    if (!/[A-Za-z]/.test(newPassword)) {
      setError('Mật khẩu phải có ít nhất một chữ cái');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setError('Mật khẩu phải có ít nhất một chữ số');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Mật khẩu xác nhận không khớp');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      await changePasswordByCccd({
        cccd: digits,
        new_password: newPassword,
      });
      setSuccess(true);
      setCccd('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err?.message || 'Không đổi được mật khẩu. Vui lòng thử lại.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative rounded-xl bg-[#C4A35A] px-2.5 py-2 text-[11px] font-semibold text-white shadow-[0_4px_10px_rgba(196,163,90,0.4)]"
      >
        Cài đặt
      </button>

      {open && (
        <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <button
            type="button"
            className="absolute inset-0 cursor-default"
            aria-label="Đóng cài đặt"
            onClick={() => setOpen(false)}
          />
          <div className="relative z-10 w-full max-w-sm overflow-hidden rounded-2xl border-2 border-[#D8B86A] bg-gradient-to-b from-[#FFFDF8] to-[#F8EFD8] shadow-xl">
            <div className="flex items-center justify-between border-b border-[#D8B86A]/60 bg-[#2B2520] px-4 py-3">
              <h2 className="text-base font-bold text-white">Cài đặt</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-xl bg-[#C4A35A] px-3 py-1.5 text-sm font-semibold text-white"
              >
                Đóng
              </button>
            </div>

            <div className="space-y-1 p-3">
              <div className="flex items-center justify-between rounded-xl border border-[#D8B86A] bg-white/70 px-3 py-3">
                <div>
                  <p className="text-sm font-medium text-black">Thông báo</p>
                  <p className="text-xs text-black/50">Nhận thông báo đẩy trên máy</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={pushOn}
                  disabled={pushBusy}
                  onClick={() => void onTogglePush()}
                  className="shrink-0"
                >
                  <span
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      pushOn ? 'bg-[#C4A35A]' : 'bg-black/20'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                        pushOn ? 'translate-x-4' : 'translate-x-0.5'
                      }`}
                    />
                  </span>
                </button>
              </div>

              <button
                type="button"
                onClick={openPasswordForm}
                className="flex w-full items-center justify-between rounded-xl px-3 py-3 text-left text-sm font-medium text-black hover:bg-black/[0.03]"
              >
                <span>Đổi mật khẩu</span>
                <i className="fas fa-key text-xs text-black/40" />
              </button>

              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  openWebPolicy();
                }}
                className="flex w-full items-center justify-between rounded-xl px-3 py-3 text-left text-sm font-medium text-black hover:bg-black/[0.03]"
              >
                <span>Điều kiện và điều khoản</span>
                <i className="fas fa-external-link-alt text-xs text-black/40" />
              </button>

              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onLogout();
                }}
                className="flex w-full items-center rounded-xl px-3 py-3 text-left text-sm font-medium text-red-600 hover:bg-red-50"
              >
                Đăng xuất
              </button>
            </div>
          </div>
        </div>
      )}

      {passwordOpen && (
        <div className="fixed inset-0 z-[95] flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <button
            type="button"
            className="absolute inset-0 cursor-default"
            aria-label="Đóng đổi mật khẩu"
            onClick={closePasswordForm}
          />
          <div className="relative z-10 w-full max-w-sm overflow-hidden rounded-2xl border-2 border-[#D8B86A] bg-gradient-to-b from-[#FFFDF8] to-[#F8EFD8] shadow-xl">
            <div className="flex items-center justify-between border-b border-[#D8B86A]/60 bg-[#2B2520] px-4 py-3">
              <h2 className="text-base font-bold text-white">Đổi mật khẩu</h2>
              <button
                type="button"
                onClick={closePasswordForm}
                className="rounded-xl bg-[#C4A35A] px-3 py-1.5 text-sm font-semibold text-white"
              >
                Đóng
              </button>
            </div>

            {success ? (
              <div className="space-y-3 p-4 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#C4A35A]/15 text-[#C4A35A]">
                  <i className="fas fa-check text-lg" />
                </div>
                <p className="text-sm font-medium text-black">Đổi mật khẩu thành công</p>
                <button
                  type="button"
                  onClick={closePasswordForm}
                  className="w-full rounded-xl bg-[#C4A35A] px-3 py-2.5 text-sm font-semibold text-white"
                >
                  Xong
                </button>
              </div>
            ) : (
              <form onSubmit={(e) => void handleChangePassword(e)} className="space-y-3 p-3">
                <div>
                  <label htmlFor="settings-cccd" className="mb-1 block text-xs font-semibold text-black/70">
                    Số CCCD
                  </label>
                  <input
                    id="settings-cccd"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={cccd}
                    onChange={(e) => setCccd(e.target.value.replace(/\D/g, '').slice(0, 12))}
                    placeholder="Nhập lại số căn cước"
                    className="w-full rounded-xl border border-[#D8B86A] bg-white px-3 py-2.5 text-sm text-black outline-none placeholder:text-black/35"
                  />
                </div>

                <div>
                  <label htmlFor="settings-new-password" className="mb-1 block text-xs font-semibold text-black/70">
                    Mật khẩu mới
                  </label>
                  <input
                    id="settings-new-password"
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Ít nhất 8 ký tự, có chữ và số"
                    className="w-full rounded-xl border border-[#D8B86A] bg-white px-3 py-2.5 text-sm text-black outline-none placeholder:text-black/35"
                  />
                </div>

                <div>
                  <label htmlFor="settings-confirm-password" className="mb-1 block text-xs font-semibold text-black/70">
                    Xác nhận mật khẩu mới
                  </label>
                  <input
                    id="settings-confirm-password"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Nhập lại mật khẩu mới"
                    className="w-full rounded-xl border border-[#D8B86A] bg-white px-3 py-2.5 text-sm text-black outline-none placeholder:text-black/35"
                  />
                </div>

                {error && (
                  <p className="rounded-xl bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-xl bg-[#C4A35A] px-3 py-3 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {submitting ? 'Đang lưu...' : 'Đổi mật khẩu'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MobileSettingsControl;
